"use client";

import React from "react";
import { Building2, User, Globe, Package, Hash, Mail, Phone, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
function RegistrationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-fade show">
        <div className="h-16 w-16 bg-[var(--color-accent)]/20 rounded-lg flex items-center justify-center mb-6">
          <CheckCircle2 className="h-8 w-8 text-[var(--color-primary)]" />
        </div>
        <h3 className="text-2xl font-default font-bold text-[var(--color-primary)] mb-2">
          Registration Successful
        </h3>
        <p className="text-[var(--color-text-dark)]/80 mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-[var(--color-text-light)]">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-8">
        {}
        <div>
          <h4 className="text-lg font-default font-semibold text-[var(--color-primary)] mb-4 border-b border-[var(--color-border)] pb-2">
            Company Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="company" rules={{
            required: "Company name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[var(--color-text-dark)]">Company Name *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input placeholder="Enter company name" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="contact" rules={{
            required: "Contact person is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[var(--color-text-dark)]">Contact Person *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input placeholder="Full name" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="country" rules={{
            required: "Country is required"
          }} render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel className="text-[var(--color-text-dark)]">Country *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Globe className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input placeholder="Country of operation" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div>
          <h4 className="text-lg font-default font-semibold text-[var(--color-primary)] mb-4 border-b border-[var(--color-border)] pb-2">
            Request Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="product" rules={{
            required: "Product of interest is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[var(--color-text-dark)]">Product of Interest *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Package className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input placeholder="e.g., Textiles, Electronics" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="quantity" rules={{
            required: "Quantity is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[var(--color-text-dark)]">Quantity Required *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Hash className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input placeholder="Estimated volume" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[var(--color-text-dark)]">Email Address *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input type="email" placeholder="work@company.com" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="whatsapp" rules={{
            required: "WhatsApp number is required",
            pattern: {
              value: /^\d+$/,
              message: "Please enter numbers only"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[var(--color-text-dark)]">WhatsApp Number *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 h-4 w-4 text-[var(--color-text-dark)]/50" />
                      <Input type="tel" placeholder="Include country code (e.g., 971...)" className="pl-10 border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[200px] bg-[var(--color-primary)] text-[var(--color-text-light)] hover:bg-[var(--color-primary)]/90 focus-visible:ring-[var(--color-accent)]" size="lg">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 h-5 w-5" />
              Processing...
            </> : 'Submit Registration'}
        </Button>
      </div>
    </Form>;
}
export default function BuyerRegistrationForm() {
  return <section id="buyerregistrationform" className="bg-[var(--color-secondary)] py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-[var(--color-primary)] mb-4">
            Buyer Registration
          </h2>
          <p className="text-[var(--color-text-dark)]/80 max-w-2xl mx-auto">
            Join our global trading network. Complete the form below to register your interest and start receiving tailored product offers.
          </p>
        </div>

        <Card className="border-[var(--color-border)] shadow-md bg-[var(--color-secondary)]">
          <CardHeader className="bg-[var(--color-primary)]/5 border-b border-[var(--color-border)] rounded-t-xl pb-6">
            <CardTitle className="text-xl font-default text-[var(--color-primary)]">Registration Form</CardTitle>
            <CardDescription className="text-[var(--color-text-dark)]/70">
              All fields marked with an asterisk (*) are required.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formId="buyer-registration-main" sectionName="buyer-registration" defaultValues={{
            company: "",
            contact: "",
            country: "",
            product: "",
            quantity: "",
            email: "",
            whatsapp: ""
          }} submitText="Submit Registration" successMessage="Thank you for registering. Our team will review your request and contact you shortly via email or WhatsApp.">
              <RegistrationFormContent />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}